import React, { useEffect, useMemo, useRef } from 'react';
import { DesignLayoutType, EventPageType, EventPageFragment } from '@graphql/generated';
import { Box, ColumnsV2, Flex, styled } from '@withjoy/joykit';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import WelcomeVideo from '../../widgets/WelcomeVideo';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { findEventPageByPageSlug, getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { useGuestSiteState } from '@apps/guest/routes/GuestSite/GuestSite.state.provider';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings';
import { PagesRefs } from '../LayoutAloha/LayoutAloha.types';
import { useAlohaScroll } from '../LayoutAloha/hooks/useAlohaScroll';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import { LayoutBrannanProps } from './LayoutBrannan';
import { PreloadImages } from '../LayoutAloha/components/PreloadImages/PreloadImages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { ContentColumns } from '../../primitives';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

const ScrollableContainer = styled(Box)`
  width: 100vw;
  height: 100vh;
  max-height: 100%;
  overflow: auto;
  z-index: 1;
`;

// Moments and RSVP are from mono repo which will be accessible via navbar but not scrolling
// Tidbits is already available as a part of page story
const PAGES_TO_BE_SKIPPED = [EventPageType.moments, EventPageType.rsvp, EventPageType.tidbits];

interface BodySectionProps {
  page: EventPageFragment;
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  isNativeAccommodations: boolean;
}

const BodySection = ({ page, event, eventHandle, websiteHeader, isNativeAccommodations }: BodySectionProps) => {
  return (
    <Body
      data-testid={`layout-body-${page.pageSlug}`}
      marginTop={[0, null, 9]}
      page={page}
      event={event}
      eventHandle={eventHandle}
      isSinglePage={true}
      photo={
        page.pageSlug === 'welcome' && event.video ? (
          <ColumnsV2.Column
            style={page.type === EventPageType.custom ? { display: isNativeAccommodations && !page?.subTitle ? 'none' : 'block' } : {}}
            span={[12, 10]}
            offset={[0, 1]}
          >
            <WelcomeVideo
              video={event.video}
              thumbnailUrl={page?.photo?.url || event.video.thumbnailUrl}
            />
          </ColumnsV2.Column>
        ) : page.pageSlug === 'welcome' && websiteHeader ? null : (
          <CoverPhotoWrapper
            renderFullColumn={page.photoPresentation?.presentationLayout !== 'center'}
            isCustomPage={page.type === EventPageType.custom}
            isNativeAccommodations={isNativeAccommodations}
            subTitle={page.subTitle}
          >
            <CoverPhoto
              websiteLayoutType={DesignLayoutType.brannan}
              photo={page.photo}
              pageId={page.id}
              pageType={page.type}
              pageSlug={page.pageSlug}
              photoPresentation={page.photoPresentation}
            />
          </CoverPhotoWrapper>
        )
      }
    />
  );
};

const PagesSection = React.memo<{
  filteredPages: EventPageFragment[];
  event: LayoutBrannanProps['event'];
  eventHandle: string;
  websiteHeader: ReturnType<typeof useWebsiteHeaderSettings>['websiteHeader'];
  isNativeAccommodations: boolean;
  pagesRef: Record<string, PagesRefs>;
}>(({ filteredPages, event, eventHandle, websiteHeader, isNativeAccommodations, pagesRef }) => {
  return (
    <>
      {filteredPages.map(page => {
        if (page.type === EventPageType.custom) {
          return page.pageSlug === 'accommodations' ? (
            <Box
              paddingTop="6rem"
              key={page.id}
              ref={ref => (pagesRef[page.id] = { ref, page })}
            >
              <Flex
                data-testid={'page-custom'}
                justifyContent="center"
                alignItems="center"
                margin={'0 auto'}
              >
                <BodySection
                  page={page}
                  event={event}
                  eventHandle={eventHandle}
                  websiteHeader={websiteHeader}
                  isNativeAccommodations={isNativeAccommodations}
                />
              </Flex>
            </Box>
          ) : (
            <Box
              paddingTop="6rem"
              key={page.id}
              ref={ref => (pagesRef[page.id] = { ref, page })}
            >
              <ContentColumns>
                <ContentColumns.Column
                  offset={[0, 1, 1]}
                  span={[12, 10, 10]}
                >
                  <Flex
                    data-testid={'page-custom'}
                    justifyContent="center"
                    alignItems="center"
                    margin={'0 auto'}
                  >
                    <BodySection
                      page={page}
                      event={event}
                      eventHandle={eventHandle}
                      websiteHeader={websiteHeader}
                      isNativeAccommodations={isNativeAccommodations}
                    />
                  </Flex>
                </ContentColumns.Column>
              </ContentColumns>
            </Box>
          );
        }

        return (
          <Box
            paddingTop="6rem"
            key={page.id}
            ref={ref => (pagesRef[page.id] = { ref, page })}
          >
            <BodySection
              page={page}
              event={event}
              eventHandle={eventHandle}
              websiteHeader={websiteHeader}
              isNativeAccommodations={isNativeAccommodations}
            />
          </Box>
        );
      })}
    </>
  );
});

const LayoutBrannanSinglePage: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme }) => {
  const { info, pages, eventDesign } = event;
  const containerRef = useRef(null);

  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;

  const filteredPages = useMemo(() => pages.filter(page => page.type !== EventPageType.tidbits && !PAGES_TO_BE_SKIPPED.includes(page.type)), [pages]);
  useAlohaScroll(filteredPages, pagesRef, containerRef.current);
  const isPreviewing = isInIframe();

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked } = useLayoutBrannanController(pages);
  const { isNativeAccommodations } = useGuestSiteState();

  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });

  const { isSmallScreen, loading } = useBrannanDesign(eventDesign);

  const pagesSection = useMemo(
    () => (
      <PagesSection
        filteredPages={filteredPages}
        event={event}
        eventHandle={eventHandle}
        websiteHeader={websiteHeader}
        isNativeAccommodations={isNativeAccommodations}
        pagesRef={pagesRef}
      />
    ),
    [filteredPages, event, eventHandle, websiteHeader, isNativeAccommodations, pagesRef],
  );
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const headerProps = useMemo(() => {
    const currentPage = currentPageSlug ? findEventPageByPageSlug(pages, currentPageSlug, isCustomPageSlug) : null;

    return {
      page: currentPage?.type,
      pageSlug: currentPage?.pageSlug,
      pageId: currentPage?.id,
      isCustomPage: currentPage?.type === EventPageType.custom,
    };
  }, [currentPageSlug, isCustomPageSlug, pages]);

  useEffect(() => {
    setLoadingTheme && setLoadingTheme(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, headerProps.pageSlug]);

  return (
    <ScrollableContainer ref={containerRef}>
      <Shell
        eventDesign={event.eventDesign}
        isCustomPage={headerProps.isCustomPage}
      >
        <PreloadImages pages={filteredPages} />
        <Box paddingTop="6rem">
          {!isNativeAccommodations && (
            <WebsiteHeader
              data-testid={'layout-header'}
              websiteHeader={websiteHeader}
              applicatorProp={componentTitleBanner.prop}
              navBarVariant={isSmallScreen ? 'menu' : 'inline'}
              eventHandle={eventHandle}
              pages={pages}
              info={info}
              handleMenuClicked={handleMenuClicked}
              handleEditEventClicked={handleEditEventClicked}
              eventProps={getEventMenuPropsFromEvent(event)}
              page={headerProps.page}
              pageSlug={headerProps.pageSlug}
              pageId={headerProps.pageId}
              eventDisplayName={info?.eventDisplayName}
            />
          )}
        </Box>
        {pagesSection}
        <Flex __css={brannanFooterContainerStyles}>
          <Footer
            onButtonClicked={handleFooterButtonClicked}
            data-testid={'layout-footer'}
          />
        </Flex>
      </Shell>
    </ScrollableContainer>
  );
};

LayoutBrannanSinglePage.displayName = 'LayoutBrannanSinglePage';

export { LayoutBrannanSinglePage };
